/** Pantalla de Inventario - Administrador y Bartender. */

import { FormEvent, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../services/api";
import { formatearPrecio } from "../utils/format";
import ConfirmModal from "../components/ConfirmModal";

interface Producto {
  id: number;
  nombre: string;
  descripcion?: string;
  precio: number;
  precio_compra?: number;
  stock: number;
  categoria_id?: number | null;
}

interface Categoria {
  id: number;
  nombre: string;
  activa: boolean;
  total_productos: number;
}

const POR_PAGINA = 20;

/**
 * Convierte lo que escribe el usuario a número.
 * Acepta formato español: "2,50" -> 2.50  |  "2.500,00" -> 2500
 * y formato inglés: "2.50" -> 2.50
 */
const parseNumero = (valor: string): number => {
  if (!valor) return 0;
  let texto = valor.trim().replace(/\s/g, "");
  if (texto.includes(",")) {
    texto = texto.replace(/\./g, "").replace(",", ".");
  }
  const numero = Number(texto);
  return isNaN(numero) ? 0 : numero;
};

/** Formatea solo dígitos con separador de miles: "3800" -> "3.800". */
const formatearMiles = (valor: string): string => {
  const digitos = valor.replace(/\D/g, "");
  if (!digitos) return "";
  return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export default function InventarioPage() {
  const usuario = JSON.parse(localStorage.getItem("user") || "{}");
  const esAdmin = usuario?.rol === "admin";
  // El Bartender no puede crear, editar ni eliminar productos.
  const soloLectura = !esAdmin;
  // El administrador siempre tiene el ingreso de stock; el Bartender lo
  // habilita temporalmente desde el menú de usuario.
  const contexto = useOutletContext<
    { ingresoStockHabilitado?: boolean } | undefined
  >();
  const mostrarIngresoStock =
    esAdmin || Boolean(contexto?.ingresoStockHabilitado);

  const [productos, setProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [buscar, setBuscar] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState<number | "">("");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [mostrarCategorias, setMostrarCategorias] = useState(false);
  const [editando, setEditando] = useState<Producto | null>(null);
  const [porEliminar, setPorEliminar] = useState<Producto | null>(null);
  const [nuevoProducto, setNuevoProducto] = useState({
    nombre: "",
    descripcion: "",
    precio: "",
    precio_compra: "",
    stock: "",
    categoria_id: "" as number | "",
  });
  const [nuevaCategoria, setNuevaCategoria] = useState("");
  const [ingresos, setIngresos] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const cargar = async () => {
    try {
      const data: any = await api.getProductos({
        buscar: buscar || undefined,
        categoria_id: categoriaFiltro || undefined,
        limite: POR_PAGINA,
        offset: pagina * POR_PAGINA,
      });
      setProductos(data.items);
      setTotal(data.total);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const cargarCategorias = async () => {
    try {
      const data: any = await api.getCategorias();
      setCategorias(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  // Una sola carga por cambio: la búsqueda espera 350 ms para no consultar
  // en cada tecla, mientras que la paginación y la categoría son inmediatas.
  useEffect(() => {
    const t = setTimeout(cargar, buscar ? 350 : 0);
    return () => clearTimeout(t);
  }, [pagina, categoriaFiltro, buscar]);

  /**
   * Precio escrito con separador de miles ("3.800"). Se eliminan los puntos
   * y cualquier símbolo antes de convertirlo, para no guardar 3.8 por error.
   */
  const parsePrecio = (valor: string): number => {
    const digitos = valor.replace(/\D/g, "");
    return digitos ? Number(digitos) : 0;
  };

  const guardarProducto = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const precio = parsePrecio(nuevoProducto.precio);
      if (precio <= 0) {
        setError("El precio debe ser mayor a cero");
        return;
      }
      const datos: any = {
        nombre: nuevoProducto.nombre.trim(),
        descripcion: nuevoProducto.descripcion.trim() || undefined,
        precio,
        // El precio de compra se registra/actualiza tanto al crear como al editar.
        precio_compra: parsePrecio(nuevoProducto.precio_compra),
        stock: parseNumero(nuevoProducto.stock || "0"),
        categoria_id: nuevoProducto.categoria_id || null,
      };
      if (editando) {
        await api.updateProducto(editando.id, datos);
        setMensaje(`Producto "${datos.nombre}" actualizado`);
      } else {
        await api.createProducto(datos);
        setMensaje(`Producto "${datos.nombre}" agregado`);
      }
      setNuevoProducto({
        nombre: "",
        descripcion: "",
        precio: "",
        precio_compra: "",
        stock: "",
        categoria_id: "",
      });
      setMostrarForm(false);
      setEditando(null);
      await Promise.all([cargar(), cargarCategorias()]);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const abrirEdicion = (p: Producto) => {
    setError(null);
    setEditando(p);
    setNuevoProducto({
      nombre: p.nombre,
      descripcion: p.descripcion || "",
      precio: formatearMiles(String(Math.round(p.precio))),
      precio_compra: p.precio_compra
        ? formatearMiles(String(Math.round(p.precio_compra)))
        : "",
      stock: String(p.stock),
      categoria_id: p.categoria_id || "",
    });
    setMostrarForm(true);
  };

  const eliminar = async () => {
    if (!porEliminar) return;
    try {
      await api.deleteProducto(porEliminar.id);
      setMensaje(`Producto "${porEliminar.nombre}" eliminado`);
      setPorEliminar(null);
      await Promise.all([cargar(), cargarCategorias()]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /** Solo admite digitos en el cuadro de ingreso de stock. */
  const actualizarIngreso = (productoId: number, valor: string) => {
    const soloNumeros = valor.replace(/\D/g, "");
    setIngresos((actual) => ({ ...actual, [productoId]: soloNumeros }));
  };

  /** Suma al stock existente la cantidad escrita por el usuario. */
  const agregarStock = async (producto: Producto) => {
    const cantidad = Number(ingresos[producto.id] || 0);
    if (!Number.isInteger(cantidad) || cantidad <= 0) {
      setMensaje(null);
      setError("Ingresá una cantidad válida mayor a cero");
      return;
    }
    try {
      // Suma sobre el stock actual de la base (no sobre el valor en pantalla).
      await api.ajustarStock(producto.id, cantidad);
      setIngresos((actual) => ({ ...actual, [producto.id]: "" }));
      setError(null);
      setMensaje(
        `Ingreso registrado: +${cantidad} ${
          cantidad === 1 ? "unidad" : "unidades"
        } de "${producto.nombre}"`
      );
      await cargar();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const ajustarStock = async (id: number, delta: number) => {
    try {
      await api.ajustarStock(id, delta);
      await cargar();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const crearCategoria = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.createCategoria(nuevaCategoria.trim());
      setNuevaCategoria("");
      await cargarCategorias();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const eliminarCategoria = async (id: number) => {
    if (!window.confirm("¿Eliminar la categoría? Los productos quedan sin categoría.")) return;
    try {
      await api.eliminarCategoria(id);
      await Promise.all([cargarCategorias(), cargar()]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const nombreCategoria = (id?: number | null) =>
    categorias.find((c) => c.id === id)?.nombre || "—";

  return (
    <div className="page">
      <div className="page-head">
        <h2>Inventario de productos</h2>
        <div className="filters">
          {!soloLectura && (
            <button
              className="btn-secondary"
              onClick={() => setMostrarCategorias(true)}
            >
              Categorías ({categorias.length})
            </button>
          )}
          {soloLectura && !mostrarIngresoStock && (
            <span className="badge badge-warning">Solo lectura</span>
          )}
          {soloLectura && mostrarIngresoStock && (
            <span className="badge badge-success">Ingreso de Stock activo</span>
          )}
          {!soloLectura && (
          <button
            className="btn-primary"
            onClick={() => {
              setError(null);
              setEditando(null);
              setNuevoProducto({
                nombre: "",
                descripcion: "",
                precio: "",
                precio_compra: "",
                stock: "",
                categoria_id: "",
              });
              setMostrarForm(true);
            }}
          >
            + Añadir producto
          </button>
          )}
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {mensaje && <p className="success-msg">{mensaje}</p>}

      {/* Filtros */}
      <div className="filtros-barra">
        <input
          type="search"
          className="input-busqueda"
          placeholder="Buscar producto..."
          value={buscar}
          onChange={(e) => {
            setBuscar(e.target.value);
            setPagina(0);
          }}
        />
        <select
          value={categoriaFiltro}
          onChange={(e) => {
            setCategoriaFiltro(e.target.value ? Number(e.target.value) : "");
            setPagina(0);
          }}
        >
          <option value="">Todas las categorías</option>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre} ({c.total_productos})
            </option>
          ))}
        </select>
        <span className="muted small">
          {total} producto{total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Tabla */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Precio</th>
              <th>Stock</th>
              {mostrarIngresoStock && <th>Ingreso de Stock</th>}
              {!soloLectura && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {productos.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>
                  <strong>{p.nombre}</strong>
                  {p.descripcion && <div className="muted small">{p.descripcion}</div>}
                </td>
                <td className="muted small">{nombreCategoria(p.categoria_id)}</td>
                <td>{formatearPrecio(p.precio)}</td>
                <td>
                  <span
                    className={
                      p.stock === 0
                        ? "badge badge-danger"
                        : p.stock < 10
                        ? "badge badge-warning"
                        : "badge badge-success"
                    }
                  >
                    {p.stock}
                  </span>
                </td>
                {mostrarIngresoStock && (
                  <td>
                    <div className="stock-editor">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="ingreso-input"
                        placeholder="Cant."
                        aria-label={`Cantidad que ingresa de ${p.nombre}`}
                        value={ingresos[p.id] ?? ""}
                        onChange={(e) =>
                          actualizarIngreso(p.id, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            agregarStock(p);
                          }
                        }}
                      />
                      <button
                        className="btn-success btn-small"
                        title={`Sumar la cantidad indicada a ${p.nombre}`}
                        onClick={() => agregarStock(p)}
                      >
                        Agregar
                      </button>
                      {esAdmin && (
                        <div className="stock-acciones">
                          <button
                            className="btn-icon"
                            title="Restar 1"
                            onClick={() => ajustarStock(p.id, -1)}
                          >
                            −
                          </button>
                          <button
                            className="btn-icon"
                            title="Sumar 1"
                            onClick={() => ajustarStock(p.id, +1)}
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                )}
                {!soloLectura && (
                  <td className="actions">
                    <button
                      className="btn-icon"
                      title="Editar"
                      onClick={() => abrirEdicion(p)}
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon danger"
                      title="Eliminar"
                      onClick={() => setPorEliminar(p)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {productos.length === 0 && (
              <tr>
                <td
                  colSpan={
                    5 +
                    (mostrarIngresoStock ? 1 : 0) +
                    (soloLectura ? 0 : 1)
                  }
                  className="empty-state"
                >
                  {buscar || categoriaFiltro
                    ? "Sin resultados para el filtro aplicado."
                    : "No hay productos registrados."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {paginas > 1 && (
        <div className="paginacion">
          <button
            className="btn-secondary btn-small"
            disabled={pagina === 0}
            onClick={() => setPagina((p) => p - 1)}
          >
            ‹ Anterior
          </button>
          <span className="muted small">
            Página {pagina + 1} de {paginas}
          </span>
          <button
            className="btn-secondary btn-small"
            disabled={pagina >= paginas - 1}
            onClick={() => setPagina((p) => p + 1)}
          >
            Siguiente ›
          </button>
        </div>
      )}

      {/* Confirmación de borrado */}
      {porEliminar && (
        <ConfirmModal
          titulo="Eliminar producto"
          mensaje={`¿Eliminar este producto? (${porEliminar.nombre})`}
          aceptar="Aceptar"
          cancelar="Cancelar"
          onConfirmar={eliminar}
          onCancelar={() => setPorEliminar(null)}
          error={error}
        />
      )}

      {/* Formulario */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={() => setMostrarForm(false)}>
          <div className="modal-box modal-producto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{editando ? `Editar: ${editando.nombre}` : "Nuevo producto"}</h3>
              <button
                className="btn-icon"
                onClick={() => setMostrarForm(false)}
                title="Cerrar"
              >
                ×
              </button>
            </div>
            <form onSubmit={guardarProducto}>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  value={nuevoProducto.nombre}
                  onChange={(e) =>
                    setNuevoProducto((p) => ({ ...p, nombre: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>Precio *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="$1.000"
                    value={nuevoProducto.precio}
                    onChange={(e) =>
                      setNuevoProducto((p) => ({
                        ...p,
                        precio: formatearMiles(e.target.value),
                      }))
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Stock</label>
                  <input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    className="input-stock"
                    value={nuevoProducto.stock}
                    onChange={(e) =>
                      setNuevoProducto((p) => ({ ...p, stock: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Categoría</label>
                  <select
                    value={nuevoProducto.categoria_id}
                    onChange={(e) =>
                      setNuevoProducto((p) => ({
                        ...p,
                        categoria_id: e.target.value ? Number(e.target.value) : "",
                      }))
                    }
                  >
                    <option value="">Sin categoría</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* El precio de compra se pide al crear y al editar, nunca en el listado */}
              <div className="form-group">
                <label>Precio de compra *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="$1.000"
                  value={nuevoProducto.precio_compra}
                  onChange={(e) =>
                    setNuevoProducto((p) => ({
                      ...p,
                      precio_compra: formatearMiles(e.target.value),
                    }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <input
                  value={nuevoProducto.descripcion}
                  onChange={(e) =>
                    setNuevoProducto((p) => ({ ...p, descripcion: e.target.value }))
                  }
                />
              </div>
              {error && <p className="error-msg">{error}</p>}
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  Guardar
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setMostrarForm(false);
                    setEditando(null);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categorías */}
      {mostrarCategorias && (
        <div className="modal-overlay" onClick={() => setMostrarCategorias(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Categorías</h3>
              <button
                className="btn-icon"
                onClick={() => setMostrarCategorias(false)}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <form className="inline-form" onSubmit={crearCategoria}>
              <div className="form-group">
                <label>Nueva categoría</label>
                <input
                  value={nuevaCategoria}
                  onChange={(e) => setNuevaCategoria(e.target.value)}
                  placeholder="Ej: Bebidas"
                />
              </div>
              <button type="submit" className="btn-primary">
                + Crear
              </button>
            </form>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Categoría</th>
                    <th>Productos</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {categorias.map((c) => (
                    <tr key={c.id}>
                      <td>{c.nombre}</td>
                      <td>{c.total_productos}</td>
                      <td className="actions">
                        <button
                          className="btn-icon danger"
                          title="Eliminar"
                          onClick={() => eliminarCategoria(c.id)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                  {categorias.length === 0 && (
                    <tr>
                      <td colSpan={3} className="empty-state">
                        Todavía no hay categorías.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
